import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import { BRAND, WHATSAPP_URL } from "@/constants/site";

const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yLogo = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const rotLogo = useTransform(scrollYProgress, [0, 1], [0, 25]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero"
      className="relative env-white pt-[92px] overflow-hidden"
    >
      {/* Ambient soft gradients — no grid, just subtle depth */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 90% 20%, rgba(0,194,209,0.10) 0%, transparent 60%), radial-gradient(ellipse 50% 40% at 85% 80%, rgba(46,229,157,0.08) 0%, transparent 60%)",
        }}
      />

      {/* Glass 3D logo — floats on the right */}
      <motion.div
        aria-hidden="true"
        style={{ y: yLogo, rotate: rotLogo }}
        className="absolute right-[-8%] md:right-[-4%] lg:right-[6%] xl:right-[10%] top-[20%] md:top-[14%] lg:top-[16%] w-[280px] sm:w-[380px] md:w-[440px] lg:w-[520px] aspect-square pointer-events-none z-0"
      >
        <div className="absolute inset-6 rounded-full bg-[#00C2D1]/25 blur-[80px]" />
        <div className="absolute inset-12 rounded-full bg-[#2EE59D]/20 blur-[60px]" />
        <motion.img
          src={BRAND.logo3D}
          alt=""
          className="relative w-full h-full object-contain"
          style={{ filter: "drop-shadow(0 25px 50px rgba(0,194,209,0.20))" }}
          animate={{ y: [0, -12, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>

      <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-8 pt-16 md:pt-24 pb-14 md:pb-20">
        <motion.h1
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.05 }}
          className="font-display text-[#1A3A61] text-[44px] sm:text-[56px] md:text-[68px] lg:text-[80px] leading-[0.98] tracking-[-0.02em] max-w-3xl"
        >
          A nova era da IA
          <br />
          <span
            className="bg-clip-text text-transparent"
            style={{
              backgroundImage: "linear-gradient(90deg, #00C2D1 0%, #2EE59D 100%)",
            }}
          >
            nas licitações.
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.15 }}
          className="mt-6 text-[#1A3A61]/70 text-lg md:text-xl leading-relaxed max-w-2xl"
        >
          A IA genérica responde. A licito.guru instrui o processo.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.25 }}
          className="mt-10 flex flex-wrap items-center gap-3"
        >
          <a
            data-testid="hero-cta-primary"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
          >
            Testar grátis
            <ArrowRight size={16} strokeWidth={2} />
          </a>
          <a
            data-testid="hero-cta-secondary"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-secondary"
          >
            Agendar Demonstração
          </a>
        </motion.div>
      </div>
    </section>
  );
}
